import * as THREE from 'three';

/**
 * Claron Formation stratigraphy as a 1-D lookup over elevation.
 * rgb = rock color (sRGB), a = hardness (resistant limestone ledges = 1, soft mudstone = 0).
 *
 * Bryce Amphitheater: the rim (~2,440 m at Sunset Point, up to 2,540 m at Inspiration/Bryce Pt) is capped by
 * paler, whiter limestone; the hoodoo zone below is the vivid "pink member": orange, salmon and red-orange
 * siltstones/mudstones interbedded with thin pale limestone ledges, with lavender/maroon mudstones lower down.
 */
export const STRATA_Y0 = 2000;
export const STRATA_RANGE = 700;
const RES = 4096;

function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type RGB = [number, number, number];
const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];

interface Zone {
  top: number;
  hard: RGB[]; // limestone colors
  soft: RGB[]; // silt/mudstone colors
  hardFrac: number;
  hardThick: [number, number];
  softThick: [number, number];
}

// Hard (limestone) beds are only modestly paler than the soft beds around them; most of the visible
// banding in Bryce is a lightness / saturation shift within the same orange-pink family.
const cream = (c: RGB, k: number): RGB => [c[0] + (240 - c[0]) * k, c[1] + (222 - c[1]) * k, c[2] + (200 - c[2]) * k];
const zone = (top: number, base: string[], hardFrac: number, hardK: number, hardThick: [number, number], softThick: [number, number]): Zone => {
  const soft = base.map(hex);
  return { top, hardFrac, hardThick, softThick, soft, hard: soft.map((c) => cream(c, hardK)) };
};

const ZONES: Zone[] = [
  // below the amphitheater floor: tan/pink siltstones with a little lavender
  zone(2235, ['#c98d70', '#be8476', '#cf9576', '#c48563', '#bf9188'], 0.25, 0.22, [0.3, 1.0], [0.8, 3.5]),
  // lower hoodoo zone: red-orange, occasional maroon interbeds
  zone(2300, ['#cf733f', '#c96b3e', '#c76f48', '#c2725f', '#d27b4a', '#d58252'], 0.3, 0.22, [0.25, 1.1], [0.6, 2.8]),
  // main hoodoo zone: vivid orange / salmon "pink member"
  zone(2385, ['#dd8147', '#e08b52', '#d97c44', '#e2955f', '#dc874f', '#d77b44', '#e39a69'], 0.34, 0.24, [0.25, 1.3], [0.5, 2.4]),
  // upper pink member below the rim: salmon, pink, cream
  zone(2440, ['#e6a47f', '#e8ad88', '#e09a6c', '#eab597', '#e4a075'], 0.4, 0.3, [0.3, 1.5], [0.4, 2.0]),
  // white member / rim caprock: cream to white limestone
  zone(2800, ['#ecc9ae', '#e6c1a3', '#eed3bd', '#e8c4a5'], 0.55, 0.45, [0.4, 2.2], [0.3, 1.4]),
];

export function buildStrataTexture(): THREE.DataTexture {
  const rnd = mulberry32(7741);
  const data = new Uint8Array(RES * 4);
  const col = new Float32Array(RES * 3);
  const hard = new Float32Array(RES);
  const mpt = STRATA_RANGE / RES; // meters per texel
  let y = STRATA_Y0;
  let lastHard = false;
  while (y < STRATA_Y0 + STRATA_RANGE) {
    const z = ZONES.find((zz) => y < zz.top) ?? ZONES[ZONES.length - 1];
    const isHard: boolean = !lastHard && rnd() < z.hardFrac * 1.6 ? true : rnd() < z.hardFrac * 0.4;
    lastHard = isHard;
    const [t0, t1] = isHard ? z.hardThick : z.softThick;
    const thick = t0 + (t1 - t0) * Math.pow(rnd(), 1.4);
    const pal = isHard ? z.hard : z.soft;
    const a = pal[Math.floor(rnd() * pal.length)];
    const b = pal[Math.floor(rnd() * pal.length)];
    const mixAB = 0.25 + rnd() * 0.5;
    const base: RGB = [a[0] * (1 - mixAB) + b[0] * mixAB, a[1] * (1 - mixAB) + b[1] * mixAB, a[2] * (1 - mixAB) + b[2] * mixAB];
    const bright = 0.96 + rnd() * 0.08;
    const h0 = isHard ? 0.75 + rnd() * 0.25 : rnd() * 0.3;
    const i0 = Math.floor((y - STRATA_Y0) / mpt);
    const i1 = Math.min(RES, Math.floor((y + thick - STRATA_Y0) / mpt));
    // thin laminae inside each bed
    let lam = 0, lamEnd = i0;
    for (let i = Math.max(0, i0); i < i1; i++) {
      if (i >= lamEnd) {
        lam = (rnd() - 0.5) * (isHard ? 0.04 : 0.07);
        lamEnd = i + 1 + Math.floor(rnd() * (0.25 / mpt));
      }
      const k = bright * (1 + lam);
      col[i * 3] = base[0] * k;
      col[i * 3 + 1] = base[1] * k;
      col[i * 3 + 2] = base[2] * k;
      hard[i] = h0;
    }
    y += thick;
  }
  // soften hardness edges (rounded ledges) and lightly smooth colors (sub-texel transitions)
  const hs = new Float32Array(RES);
  const R = 3;
  for (let i = 0; i < RES; i++) {
    let s = 0, w = 0;
    for (let k = -R; k <= R; k++) {
      const j = Math.min(RES - 1, Math.max(0, i + k));
      const ww = 1 - Math.abs(k) / (R + 1);
      s += hard[j] * ww; w += ww;
    }
    hs[i] = s / w;
  }
  for (let i = 0; i < RES; i++) {
    const j0 = Math.max(0, i - 1), j1 = Math.min(RES - 1, i + 1);
    for (let c = 0; c < 3; c++) {
      const v = (col[j0 * 3 + c] + 2 * col[i * 3 + c] + col[j1 * 3 + c]) / 4;
      data[i * 4 + c] = Math.max(0, Math.min(255, Math.round(v)));
    }
    data[i * 4 + 3] = Math.round(hs[i] * 255);
  }
  const tex = new THREE.DataTexture(data, RES, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/** GLSL helpers for strata lookups. Requires uStrata / uStrataRange uniforms and noise. */
export const strataGLSL = /* glsl */ `
uniform sampler2D uStrata;
uniform vec2 uStrataRange;
// bedding is close to horizontal; gentle regional dip + slow undulation
float strataY(vec3 wp) {
  float warp = (vnoise2(wp.xz * 0.0031) - 0.5) * 4.0 + (vnoise2(wp.xz * 0.019 + 7.0) - 0.5) * 0.9;
  return wp.y + warp - wp.x * 0.0022 + wp.z * 0.0012;
}
vec4 strataAt(float y) {
  return texture(uStrata, vec2((y - uStrataRange.x) / uStrataRange.y, 0.5));
}
vec4 strataAtLod(float y, float lod) {
  return textureLod(uStrata, vec2((y - uStrataRange.x) / uStrataRange.y, 0.5), lod);
}
`;
